import { useEffect, useState } from 'react';
import {
  Form,
  Row,
  Col,
  InputGroup,
  Button,
  Card,
  Alert,
} from 'react-bootstrap';
import {
  useCreateTicketingRequest,
  useGetTicketServiceList,
} from '../apiEndpoints/VisitorApiEndpoints';
import Toaster from '../../../Components/Toaster/Toaster';
import { useNavigate } from 'react-router-dom';
import { MdOutlineCancel } from 'react-icons/md';
const Toast = Toaster();

const CreateTicketingRequest = ({
  handleClose,
}: {
  handleClose?: () => void;
}) => {
  const navigate = useNavigate();
  const { data } = useGetTicketServiceList();
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const { mutate, isSuccess, data: ticketCreate } = useCreateTicketingRequest();

  const fixedDates = [
    { date: '2026-09-17', label: 'September, 17 2026' },
    { date: '2026-09-18', label: 'September, 18 2026' },
    { date: '2026-09-19', label: 'September, 19 2026' },
  ];

  const handleDateSelection = (date: string) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date],
    );
  };

  const handleQuantityChange = (date: string, qty: number) => {
    const price = Number(data?.data[0]?.amount);
    setTotals((prev) => ({ ...prev, [date]: Math.max(0, qty || 1) * price }));
    setQuantities((prev) => ({ ...prev, [date]: qty }));
  };

  const handleCancelDate = (date: string) => {
    setSelectedDates((prev) => prev.filter((d) => d !== date));
    setTotals((prev) => {
      const updatedTotals = { ...prev };
      delete updatedTotals[date];
      return updatedTotals;
    });
    setQuantities((prev) => {
      const updatedQuantities = { ...prev };
      delete updatedQuantities[date];
      return updatedQuantities;
    });
  };

  useEffect(() => {
    if (data?.data) {
      const defaultPrice = Number(data?.data[0]?.amount);

      // Update totals and quantities only for newly selected dates
      setTotals((prevTotals) => {
        const newTotals = selectedDates.reduce(
          (acc, date) => {
            if (!(date in prevTotals)) {
              acc[date] = defaultPrice; // Add only new date with default price
            }
            return acc;
          },
          {} as Record<string, number>,
        );
        return { ...prevTotals, ...newTotals }; // Merge with previous totals
      });

      setQuantities((prevQuantities) => {
        const newQuantities = selectedDates.reduce(
          (acc, date) => {
            if (!(date in prevQuantities)) {
              acc[date] = 1; // Add only new date with default quantity
            }
            return acc;
          },
          {} as Record<string, number>,
        );
        return { ...prevQuantities, ...newQuantities }; // Merge with previous quantities
      });
    }
  }, [data?.data, selectedDates]);

  useEffect(() => {
    if (isSuccess && ticketCreate) {
      handleClose?.();

      if (ticketCreate?.data?.invoice_id) {
        navigate(
          `/visitor/view-billing-info/${ticketCreate?.data?.invoice_id}`,
        );
      }
    }
  }, [isSuccess, ticketCreate]);

  const handleSubmit = () => {
    if (selectedDates.length > 0 && data?.data[0]?.id) {
      const output = selectedDates.map((date) => ({
        date,
        quantity: quantities[date],
        total: totals[date],
        service_id: data?.data[0]?.id,
      }));

      mutate(output);
    } else {
      Toast.fire({
        icon: 'error',
        title: `Please select at least one date`,
      });
    }
  };

  return (
    <Card className='p-3 shadow-sm'>
      <Card.Title className='text-center mb-4'>Ticketing Request</Card.Title>
      <Form>
        <Row className='mb-3'>
          <Col>
            <Form.Label style={{ fontWeight: 'bold' }}>
              Select Dates for visiting fair.
            </Form.Label>
            {fixedDates?.map((item) => (
              <div>
                <Form.Check
                  key={item.date}
                  type='checkbox'
                  label={item.label}
                  value={item.date}
                  checked={selectedDates.includes(item.date)}
                  onChange={() => handleDateSelection(item.date)}
                />
              </div>
            ))}
          </Col>
        </Row>

        {Boolean(selectedDates?.length) && (
          <Alert variant='warning' className='text-center'>
            <strong>Note:</strong> Children under 14 can enter for{' '}
            <span className='text-success'>free</span>, but those 14 and older
            need a <span className='text-danger'>Ticket</span>.
          </Alert>
        )}

        {selectedDates?.map((date) => (
          <div
            key={date}
            className='mb-4 p-3 rounded'
            style={{ background: '#f7f7f7' }}
          >
            <Card.Title className='d-flex align-items-center justify-content-between mb-2'>
              <span className='d-flex align-items-center flex-grow-1'></span>
              <span className='d-flex align-items-center flex-grow-1'>
                Ticket Request for{' '}
                {fixedDates.find((d) => d.date === date)?.label}
              </span>
              <span
                className='text-danger pointer'
                onClick={() => handleCancelDate(date)}
              >
                <MdOutlineCancel
                  size={30}
                  onClick={() => handleCancelDate(date)}
                />
              </span>
            </Card.Title>

            <Row className='mb-3'>
              <Col>
                <Form.Label>Per Ticket Price</Form.Label>
                <Form.Select disabled>
                  {data?.data?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({item.amount})
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col>
                <Form.Label>Quantity</Form.Label>
                <InputGroup>
                  <Form.Control
                    required
                    type='number'
                    min='0'
                    defaultValue={1}
                    onChange={(e) =>
                      handleQuantityChange(date, Number(e.target.value))
                    }
                  />
                </InputGroup>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col>
                <h5 className='text-center'>
                  Total:{' '}
                  <span className='text-primary'>
                    {totals[date]?.toFixed(2)}
                  </span>
                </h5>
              </Col>
            </Row>
          </div>
        ))}
        <Row className='text-center'>
          <Col>
            <Button
              variant='primary'
              onClick={handleSubmit}
              className='submit-button common-btn'
            >
              Submit Request
            </Button>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default CreateTicketingRequest;
